<template>
    <b-container fluid class="mt-md-4 px-0 px-md-4">
        <b-tabs
            @input="onTabInput"
            class="mx-0"
            nav-wrapper-class="d-none d-md-block"
            v-model="tabIndex"
            justified
        >
            <portal to="sticky-header" :order="6">
                <scrollable-widget
                    arrow-bg-color="white"
                    class="tab-nav-wrapper d-block d-md-none pt-1 bg-white">
                    <b-nav justified class="d-flex flex-nowrap nav-tabs">
                        <b-nav-item
                            v-for="(tab, key) in data"
                            :key="key"
                            @click.prevent="tabIndex = key"
                            :active="tabIndex === key"
                            href="#"
                        >
                            {{ tab.title }}
                        </b-nav-item>
                    </b-nav>
                </scrollable-widget>
            </portal>
            <b-tab v-for="item in data" :title="item.title" :key="item.title">
                <b-row>
                    <b-col v-for="(chart, index) in item.data" cols="12" :lg="getColSize(index)" class="pb-5 pb-lg-0" style="min-width: 160px;">
                        <vue-element-loading :active="chart.loading" />
                        <div :class="'chart-container ' + chart.containerClass"
                             v-if="!chart.loading">
                            <h5>{{ chart.title }}</h5>
                            <div class="small">Total {{ chart.actionCount }} actions</div>
                            <div class="small pb-3">Total {{ chart.propertyCount }} properties</div>

                            <data-pie
                                @set-legend="item.key = $event"
                                :chart-data="chart.chartData"
                                :colours="chartColours"
                                :options="chartOptions"
                                @click="gotoActions"
                            >
                            </data-pie>
                        </div>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <div class="chart-key-container">
                            <div v-if="item.loaded && hasData"
                                 v-for="(key, i) in item.key"
                                 :key="i"
                                 class="chart-key">
                                <span class="circle"
                                      :style="'background-color:' + mappedColors(key.label) +';'"></span>
                                {{ key.name }}
                            </div>
                            <div v-if="!item.loaded" class="text-center">
                                Loading...
                            </div>
                        </div>
                    </b-col>
                </b-row>
            </b-tab>
        </b-tabs>
    </b-container>
</template>

<script>
import ActionItem from '@/components/Action/Action';
import DataPie from '@/components/Charts/DataPie';
import Report from '@/services/Report/Report';
import colours from '@/config/colours';
import ScrollableWidget from '@/components/Widgets/ScrollableWidget';
import {mapGetters} from "vuex";
import ActionStatus from '@/services/Action/ActionStatus';

export default {
  name: 'Dashboard',
  components: {
    ActionItem,
    DataPie,
    ScrollableWidget,
  },
  data() {
    return {
      tabIndex: 0,
      loading: true,
      hasData: true,
      chartColours: colours,
      mappedColors: key => colours[key],
      chartOptions: {
        hover: {
          onHover(e) {
            e.target.style.cursor = 'pointer';
          },
        },
      },
      data: [
        {
          title: 'Property Classifications',
          loaded: false,
          shown: false,
          key: [],
          labels: [],
          type: 'status',
          data: [
            {
              title: 'All Properties',
              riskProfile: 'combined',
              chartData: [],
              loading: false,
              containerClass: 'lg',
            },
            {
              title: 'Property Classification Level 1',
              riskProfile: 'high',
              chartData: [],
              loading: false,
              containerClass: '',
            },
            {
              title: 'Property Classification Level 2',
              riskProfile: 'medium',
              chartData: [],
              loading: false,
              containerClass: '',
            },
            {
              title: 'Property Classification Level 3',
              riskProfile: 'low',
              chartData: [],
              loading: false,
              containerClass: '',
            },
          ],
        },
        {
          title: 'Outstanding Actions (Not Overdue)',
          loaded: false,
          shown: false,
          key: [],
          labels: [],
          type: 'actionTypes',
          data: [
            {
              title: 'All Properties',
              riskProfile: 'combined',
              chartData: [],
              loading: false,
              containerClass: 'lg',
            },
            {
              title: 'Property Classification Level 1',
              riskProfile: 'high',
              chartData: [],
              loading: false,
              containerClass: '',
            },
            {
              title: 'Property Classification Level 2',
              riskProfile: 'medium',
              chartData: [],
              loading: false,
              containerClass: '',
            },
            {
              title: 'Property Classification Level 3',
              riskProfile: 'low',
              chartData: [],
              loading: false,
              containerClass: '',
            },
          ],
        },
        {
          title: 'Outstanding Actions (Overdue)',
          loaded: false,
          shown: false,
          key: [],
          labels: [],
          type: 'overdue',
          data: [
            {
              title: 'All Properties',
              riskProfile: 'combined',
              chartData: [],
              loading: false,
              containerClass: 'lg',
            },
            {
              title: 'Property Classification Level 1',
              riskProfile: 'high',
              chartData: [],
              loading: false,
              containerClass: '',
            },
            {
              title: 'Property Classification Level 2',
              riskProfile: 'medium',
              chartData: [],
              loading: false,
              containerClass: '',
            },
            {
              title: 'Property Classification Level 3',
              riskProfile: 'low',
              chartData: [],
              loading: false,
              containerClass: '',
            },
          ],
        },
      ],
    };
  },
  methods: {
    getData(index) {
      const activeTab = this.data[index];
      const requests = activeTab.data;

      requests.forEach(async (item, i) => {
        requests[i].loading = true;
        try {
          const response = await Report.get(activeTab.type, item.riskProfile, this.$store.getters.complianceTypeId, this.$store.getters.propertyTenure);
          const { data } = response.data;

          requests[i].chartData = data;
          const { series } = data;

          let counts = 0;
          this.hasData = false;
          requests[i].actionCount = 0;
          requests[i].propertyCount = 0;

          if (series) {
              this.hasData = true;

              counts = series.data.map(row => row.count);
              requests[i].actionCount = counts.reduce((total, number) => total + number);
              requests[i].propertyCount = series.propertyCount;
          }
        } catch (e) {
          // TODO: Something here
        } finally {
          requests[i].loading = false;
        }
      });

      activeTab.loaded = true;
    },

    onTabInput(index) {
      this.getData(index);
      this.data[index].shown = true;
    },

    getColSize(index) {
      if (index === 0) {
        return 5;
      }

      return 2;
    },
    gotoActions(params) {
      const { item, chartData} = params;
      const index = item._index;
      const { series, type, riskProfile } = chartData;

      const data = series.data[index];
      const value = data.name;
      const query = {};

      if (riskProfile !== 'combined') {
        query.riskProfile = this.getRiskProfile(riskProfile);
      }

      if (type === 'status') {
        const allStatuses = ActionStatus.getAllStatus();
        const status = value.toLowerCase();

        if (status === 'completed') {
          query.status = ['approved'];
          this.$router.push({
            name: 'actions',
            query,
          });
          return;
        }

        if (allStatuses.includes(status)) {
          const statusFilter = ActionStatus.getRouteByStatus(status);
          this.$router.push({
            name: 'actionStatus',
            params: { statusFilter },
            query,
          });
          return;
        }
        // Outstanding / Overdue
        query.overdue = 1;
        if (status.includes('not overdue')) {
          query.overdue = 2;
        }
      } else if (type === 'overdue' || type === 'actionTypes') {
        query.hideStatus = ['approved'];
        query.priority = value;
        query.overdue = 2;

        if (type === 'overdue') {
          query.overdue = 1;
        }
      }

      this.$router.push({
        name: 'actions',
        query,
      });
    },
    getRiskProfile(riskProfile) {
      if (riskProfile === 'Level 3') {
        return [
          'Level 3',
          'Level 4',
        ];
      }
      return riskProfile;
    },
  },
  watch: {
    'typeId': function() {
      this.getData(this.tabIndex);
     },
    '$store.getters.propertyTenure': function() {
      this.getData(this.tabIndex);
    },
  },
  computed: {
      ...mapGetters({ typeId: 'assessmentTypeId' }),
  },
  created() {
    this.onTabInput(this.tabIndex);
  },
};
</script>

<style lang="scss">
.chart-container {
    text-align: center;
}

.chart-key-container {
    width: 300px;
    margin: 30px auto auto;
    padding: 20px;
    border-radius: 3px;
    border: 1px solid $border-color;
}

.chart-key {

}

.circle {
    width: 17.5px;
    height: 17.5px;
    border-radius: 50%;
    border: 1px solid $border-color;
    display: inline-block;
    vertical-align: text-bottom;
}

@include media-breakpoint-up(lg) {
    .nav-tabs {
        .nav-item {
            flex-basis: 310px;
        }
    }
}
</style>
